import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { IS_PRERENDER } from '@/lib/prerender';

const AuthContext = createContext();

const API_BASE_URL = 'https://api.threat.live/client';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check auth status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Check authentication status using HttpOnly cookie
  const checkAuthStatus = async () => {
    if (IS_PRERENDER) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.user) {
          setUser(data.user);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  // API helper function - uses HttpOnly cookies
  const apiCall = async (endpoint, options = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();
    
    if (response.status === 401) {
      setUser(null);
      setIsAuthenticated(false);
      throw new Error(data.message || 'Authentication required');
    }
    
    if (!response.ok) {
      throw new Error(data.message || 'API request failed');
    }

    return data;
  };

  // Register - Step 1
  const register = async (userData) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    return await response.json();
  };

  // Verify OTP - Step 2
  const verifyOtp = async (email, otpCode) => {
    const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp_code: otpCode }),
    });
    
    const data = await response.json();

    if (data.status === 'success') {
      // Cookie is set by backend, check auth status
      await checkAuthStatus();
    }

    return data;
  };

  // Login
  const login = async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (response.ok && data.status === 'success') {
      // Cookie is set by backend automatically
      // Set user from response and mark as authenticated
      if (data.user) {
        setUser(data.user);
        setIsAuthenticated(true);
      } else {
        // Fallback: check auth status
        await checkAuthStatus();
      }
    }

    return data;
  };

  // Logout
  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      // Ignore errors
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  // Forgot Password
  const forgotPassword = async (email) => {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    // HTTP durumu da dönsün: sunucu hatası (404/500/429) eskiden "gönderildi" gibi görünüyordu
    const data = await response.json().catch(() => ({}));
    return { ...data, httpStatus: response.status };
  };

  // Google OAuth Login
  const googleLogin = async () => {
    const response = await fetch(`${API_BASE_URL}/auth/google-login`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json();
    
    if (data.status === 'success' && data.auth_url) {
      window.location.href = data.auth_url;
    } else {
      throw new Error(data.message || 'Google OAuth başlatılamadı');
    }
  };

  // Handle Google OAuth Callback - token comes in URL, cookie is set by redirect
  const handleGoogleCallback = async (token) => {
    // For Google OAuth, the token in URL is just for verification
    // The actual session cookie should already be set by the API redirect
    // We just need to verify the session
    try {
      await checkAuthStatus();
      if (!isAuthenticated) {
        throw new Error('Session verification failed');
      }
      return { success: true, user };
    } catch (error) {
      throw error;
    }
  };

  // Reset Password
  const resetPassword = async (resetToken, newPassword) => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resetToken, new_password: newPassword }),
    });
    return await response.json();
  };

  // Change Password
  const changePassword = async (currentPassword, newPassword) => {
    return await apiCall('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  };

  // Change Email - Step 1
  const changeEmailStep1 = async (newEmail) => {
    return await apiCall('/auth/change-email', {
      method: 'POST',
      body: JSON.stringify({ step: 1, new_email: newEmail }),
    });
  };

  // Change Email - Step 2
  const changeEmailStep2 = async (emailToken, otpCode) => {
    return await apiCall('/auth/change-email', {
      method: 'POST',
      body: JSON.stringify({ step: 2, token: emailToken, otp_code: otpCode }),
    });
  };

  // AS Management
  const getAbuseContacts = async (asn) => {
    return await apiCall('/dashboard/as/add-step1', {
      method: 'POST',
      body: JSON.stringify({ asn }),
    });
  };

  const addAS = async (asn, abuseEmail) => {
    return await apiCall('/dashboard/as/add-step2', {
      method: 'POST',
      body: JSON.stringify({ asn, abuse_email: abuseEmail }),
    });
  };

  const getASList = async () => {
    return await apiCall('/dashboard/as/list', { method: 'GET' });
  };

  const removeAS = async (asnId) => {
    return await apiCall('/dashboard/as/remove', {
      method: 'DELETE',
      body: JSON.stringify({ asn_id: asnId }),
    });
  };

  // Resend AS Verification Email
  const resendASVerification = async (asnId) => {
    return await apiCall('/dashboard/as/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ asn_id: asnId }),
    });
  };

  // BGP Management
  const addBGPSession = async (sessionData) => {
    return await apiCall('/dashboard/bgp/add', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  };

  const getBGPList = async (asnId = null) => {
    const endpoint = asnId 
      ? `/dashboard/bgp/list?asn_id=${asnId}`
      : '/dashboard/bgp/list';
    return await apiCall(endpoint, { method: 'GET' });
  };

  const removeBGPSession = async (bgpId) => {
    return await apiCall('/dashboard/bgp/remove', {
      method: 'DELETE',
      body: JSON.stringify({ bgp_id: bgpId }),
    });
  };

  // Webhook Management
  const addWebhook = async (webhookUrl) => {
    return await apiCall('/dashboard/webhook/add', {
      method: 'POST',
      body: JSON.stringify({ webhook_url: webhookUrl }),
    });
  };

  const getWebhookList = async () => {
    return await apiCall('/dashboard/webhook/list', { method: 'GET' });
  };

  const removeWebhook = async (webhookId) => {
    return await apiCall('/dashboard/webhook/remove', {
      method: 'DELETE',
      body: JSON.stringify({ webhook_id: webhookId }),
    });
  };

  // Delist Management
  const checkDelistIP = async (ip) => {
    return await apiCall(`/delist/check?ip=${encodeURIComponent(ip)}`, { method: 'GET' });
  };

  const requestDelist = async (delistData) => {
    return await apiCall('/delist/request', {
      method: 'POST',
      body: JSON.stringify(delistData),
    });
  };

  // Admin Statistics
  const getAdminStats = async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.type) queryParams.append('type', params.type);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.asn) queryParams.append('asn', params.asn);
    if (params.node_id) queryParams.append('node_id', params.node_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    const url = `/admin/statistics.php${queryString ? `?${queryString}` : ''}`;
    return await apiCall(url, { method: 'GET' });
  };

  // Admin IP Search
  const adminIpSearch = async (searchData) => {
    return await apiCall('/admin/ip-search.php', {
      method: 'POST',
      body: JSON.stringify(searchData),
    });
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    // Auth methods
    checkAuthStatus,
    register,
    verifyOtp,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    changeEmailStep1,
    changeEmailStep2,
    googleLogin,
    handleGoogleCallback,
    // AS methods
    getAbuseContacts,
    addAS,
    getASList,
    removeAS,
    resendASVerification,
    // BGP methods
    addBGPSession,
    getBGPList,
    removeBGPSession,
    // Webhook methods
    addWebhook,
    getWebhookList,
    removeWebhook,
    // Delist methods
    checkDelistIP,
    requestDelist,
    // Admin methods
    getAdminStats,
    adminIpSearch,
    // API helper
    apiCall,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
